/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useMemo, useContext } from 'react';

import { PrimaryCarousel, ContentBlock, Icon } from '@powdr/components';
import {
  CarouselControlPlacement,
  getIndicatorType,
  handleCustomIndicators,
} from '@powdr/constants';
import { AppContext } from '@powdr/context';
import { useSeason } from '@powdr/hooks';
import { colorProfileByIndex } from '@powdr/utils';

import { StyledContentBlockCarousel, CarouselIcon } from './styles';

export const ContentBlockCarousel = ({
  id = '',
  iconName = '',
  blockType = '',
  customSlideIndicators = 0,
  scrollingIndicators = false,
  isHorizontalScroll = false,
  slidesToShow = null,
  spaceBetweenSlides = 0,
  colorProfile = null,
  verticalAlignAll = null,
  relationships = null,
  parentColorProfile = null,
}) => {
  const {
    isMobile,
  } = useContext(AppContext);
  const { checkSeasonality } = useSeason();
  const { contentBlocks } = relationships;
  const trueColorProfile = (colorProfile !== null)
    ? (typeof colorProfile === 'number') ? colorProfileByIndex(colorProfile) : colorProfile
    : parentColorProfile;
  const slidesToShowParsed = (slidesToShow) ? parseInt(slidesToShow, 10) : 1;
  const isDesktopHorizontalScroll = !!(isHorizontalScroll && !isMobile);
  const horizontalScrollGap = spaceBetweenSlides || 30;

  const contentBlockMap = useMemo(
    () => contentBlocks.map((contentBlock, index) => {
      const { id: blockId, season, isHideBlock } = contentBlock;

      if (checkSeasonality(season) && !isHideBlock) {
        return (
          <ContentBlock
            contentBlock={contentBlock}
            key={blockId}
            index={index}
            columnOption={1}
            blockType={blockType}
            colorProfile={trueColorProfile}
            verticalAlignment={verticalAlignAll}
            carouselAlignmentTweak={!!verticalAlignAll}
          />
        );
      }

      return null;
    }).filter((x) => x),
    [contentBlocks, blockType, checkSeasonality, trueColorProfile, verticalAlignAll],
  );

  const horizontalSlideCount = Math.min(
    slidesToShowParsed,
    contentBlockMap.length || slidesToShowParsed,
  ) || 1;

  if (contentBlockMap?.length === 0 || !blockType) return null;

  return (
    <StyledContentBlockCarousel
      key={id}
      $isHorizontalScroll={isDesktopHorizontalScroll}
      $slidesToShow={horizontalSlideCount}
      $spaceBetween={horizontalScrollGap}
    >
      {iconName && (
        <CarouselIcon colorProfile={parentColorProfile}>
          <Icon name={iconName} height="50" />
        </CarouselIcon>
      )}
      {isDesktopHorizontalScroll ? (
        <PrimaryCarousel
          // Swiper only reads `breakpoints` when it is constructed, so the two
          // branches must not share a Swiper instance when isMobile flips.
          key="horizontal-scroll"
          colorProfile={parentColorProfile}
          isInfiniteScroll={false}
          slidesPerView="auto"
          fitSlideWidthToItem
          controlSettings={{
            scrollbar: {
              placement: CarouselControlPlacement.BOTTOM,
            },
          }}
          spaceBetweenSlides={horizontalScrollGap}
        >
          {contentBlockMap}
        </PrimaryCarousel>
      ) : (
        <PrimaryCarousel
          key="standard"
          colorProfile={parentColorProfile}
          controlSettings={(isMobile || (!isMobile && contentBlockMap.length > slidesToShowParsed))
            && {
              navigation: {
                iconName: 'ui-arrow-circle-left',
                iconSize: 45,
                placement: CarouselControlPlacement.BOTTOM,
              },
              pagination: (handleCustomIndicators(
                contentBlockMap.map((e) => e.props.contentBlock),
                getIndicatorType(customSlideIndicators),
              )?.length > 0) ? {
                  type: getIndicatorType(customSlideIndicators),
                  placement: CarouselControlPlacement.TOP,
                  customBullets: handleCustomIndicators(
                    contentBlockMap.map((e) => e.props.contentBlock),
                    getIndicatorType(customSlideIndicators),
                  ),
                  scrollingIndicators,
                } : null,
            }}
          breakpoints={{
            320: {
              slidesPerView: 1,
            },
            960: {
              slidesPerView: (contentBlockMap.length > slidesToShowParsed)
                ? slidesToShowParsed
                : contentBlockMap.length,
            },
          }}
          slidesPerView={(contentBlockMap.length > slidesToShowParsed)
            ? slidesToShowParsed
            : contentBlockMap.length}
          spaceBetweenSlides={spaceBetweenSlides || 0}

        >
          {contentBlockMap}
        </PrimaryCarousel>
      )}
    </StyledContentBlockCarousel>
  );
};

ContentBlockCarousel.propTypes = {
  id: PropTypes.string,
  iconName: PropTypes.string,
  blockType: PropTypes.string,
  customSlideIndicators: PropTypes.number,
  scrollingIndicators: PropTypes.bool,
  isHorizontalScroll: PropTypes.bool,
  verticalAlignAll: PropTypes.string,
  slidesToShow: PropTypes.string,
  spaceBetweenSlides: PropTypes.number,
  colorProfile: PropTypes.number,
  parentColorProfile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  relationships: PropTypes.shape(),
};
